/**
 * Per-tenant theme application for Anagram. Sets the same --sg-* CSS vars
 * the WhatIs / Survey-Says modules use so streamers' tuned palette carries
 * across games.
 */
import type { ThemePalette } from '@sg/shared';

function buildDefaultTheme(): ThemePalette {
  const t: Partial<ThemePalette> = {};
  t.primary = '#FFC43D';
  t.secondary = '#060CE9';
  t.accent = '#00BFA6';
  t.background = '#0A0E27';
  t.text_on_primary = '#060CE9';
  t.text_on_secondary = '#FFC43D';
  t.tile_bg = '#060CE9';
  t.celebration = '#22C55E';
  return t as ThemePalette;
}

/** Anagram default palette — yellow-on-blue brand with a teal accent. */
export const DEFAULT_THEME: ThemePalette = buildDefaultTheme();

/** Map from theme key to its CSS custom-property name on document root. */
export type ThemeVarKey =
  | 'primary' | 'secondary' | 'accent' | 'background'
  | 'text_on_primary' | 'text_on_secondary' | 'tile_bg' | 'celebration';

const THEME_VAR_MAP = new Map<ThemeVarKey, string>([
  ['primary', '--sg-primary'],
  ['secondary', '--sg-secondary'],
  ['accent', '--sg-accent'],
  ['background', '--sg-bg'],
  ['text_on_primary', '--sg-text-on-primary'],
  ['text_on_secondary', '--sg-text-on-secondary'],
  ['tile_bg', '--sg-tile-bg'],
  ['celebration', '--sg-celebration'],
]);

export function applyTheme(theme: ThemePalette): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty(THEME_VAR_MAP.get('primary')!, theme.primary);
  root.style.setProperty(THEME_VAR_MAP.get('secondary')!, theme.secondary);
  root.style.setProperty(THEME_VAR_MAP.get('accent')!, theme.accent);
  root.style.setProperty(THEME_VAR_MAP.get('background')!, theme.background);
  root.style.setProperty(THEME_VAR_MAP.get('text_on_primary')!, theme.text_on_primary);
  root.style.setProperty(THEME_VAR_MAP.get('text_on_secondary')!, theme.text_on_secondary);
  root.style.setProperty(THEME_VAR_MAP.get('tile_bg')!, theme.tile_bg);
  root.style.setProperty(THEME_VAR_MAP.get('celebration')!, theme.celebration);
}

/**
 * Read a theme CSS custom property as a `#RRGGBB` string. Used by the canvas
 * sparkle module that can't bind CSS vars directly. Falls back to `fallback`
 * when unset or malformed.
 */
export function getThemeVar(name: ThemeVarKey, fallback: string): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback;
  }
  const varName = THEME_VAR_MAP.get(name);
  if (!varName) return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : fallback;
}
